import * as _ from "lodash";
import { HeaderParameter, Operation, Path, PathParameter, QueryParameter, Schema, Spec } from "swagger-schema-official";
import { getElementByJsonReference } from "../../shared/SwaggerUtils";
import { HttpApiTriggerConfig } from "../components/Triggers/HttpApiTrigger";
import { Triggers } from "../model/ITriggerData";
import { IElement } from "./IProcessData";

export const constantTriggerReferences = ["type", "name", "username"];
export function getTriggerReferences(elements: IElement[], manualTriggerParameterNames: string[] | null, isForMonitoringRule?: boolean) {
    const triggers = elements.map((t) => {
        switch (t.selectedTypeId) {
            case Triggers.ManualTrigger.triggerType:
                if (isForMonitoringRule) {
                    return [
                        "data.monitoringRule",
                        "data.monitoringRule.name",
                        "data.monitoringRule.variableName",
                        "data.monitoringRule.emailRecipients",
                        "data.monitoringRule.triggeredProcesses",
                        "data.monitoringRule.expectedValue",
                        "data.monitoringRule.error"
                    ];
                } else {
                    return [];
                }
            case Triggers.HttpTrigger.triggerType:
                {
                    const passRawContentBytes = t.parameters["passRawContentBytes"];
                    const refs = [
                        "data",
                        "data.username",
                        passRawContentBytes ? "data.httpContentBytesInBase64" : "data.httpBody",
                        "data.httpParameters",
                        "data.httpHeaders",
                        "data.queryParameters",
                        "data.pathParameters",
                        "data.httpMethod",
                        "data.httpRequestUri",
                        "data.httpClientIp",
                        "data.httpCookies"
                    ];
                    const uriParamRegex = /{\*?([^}]*)}/g;
                    let uriParamsMatch = uriParamRegex.exec(t.parameters["routeTemplate"]);
                    while (uriParamsMatch !== null) {
                        refs.push("data.httpParameters." + uriParamsMatch[1]); // is matched text, [1] i capture group
                        refs.push("data.pathParameters." + uriParamsMatch[1]);
                        uriParamsMatch = uriParamRegex.exec(t.parameters["routeTemplate"]);
                    }

                    return refs;
                }
            case Triggers.FileWatchTrigger.triggerType:
                return ["data", "data.files", "data.filePaths"];
            case Triggers.QueueTrigger.triggerType:
                return ["data",
                    "data.applicationProperties",
                    "data.body",
                    "data.properties",
                    "data.properties.messageId",
                    "data.properties.absoluteExpiryTime",
                    "data.properties.contentEncoding",
                    "data.properties.contentType",
                    "data.properties.correlationId",
                    "data.properties.creationTime",
                    "data.properties.groupId",
                    "data.properties.groupSequence",
                    "data.properties.replyToGroupId",
                    "data.properties.replyTo",
                    "data.properties.subject",
                    "data.properties.userId",
                    "data.properties.to"];
            case Triggers.RabbitMQTrigger.triggerType:
                return [
                    "data",
                    "data.BasicProperties",
                    "data.BasicProperties.Headers",
                    "data.BasicProperties.AppId",
                    "data.BasicProperties.ClusterId",
                    "data.BasicProperties.ContentEncoding",
                    "data.BasicProperties.ContentType",
                    "data.BasicProperties.CorrelationId,",
                    "data.BasicProperties.DeliveryMode",
                    "data.BasicProperties.Expiration",
                    "data.BasicProperties.MessageId",
                    "data.BasicProperties.Persistent",
                    "data.BasicProperties.Priority",
                    "data.BasicProperties.ProtocolClassId",
                    "data.BasicProperties.ProtocolClassName",
                    "data.BasicProperties.ReplyTo",
                    "data.BasicProperties.ReplyToAddess",
                    "data.BasicProperties.Timestamp",
                    "data.BasicProperties.Type",
                    "data.BasicProperties.UserId",
                    "data.Body",
                    "data.ConsumerTag",
                    "data.DeliveryTag",
                    "data.Exchange",
                    "data.Redelivered",
                    "data.RoutingKey",
                ]
            case Triggers.ServiceBusTrigger.triggerType:
                return ["data", "data.properties", "data.body"];
            case Triggers.HttpApiTrigger.triggerType:
                {
                    const apiParameters = t.parameters as HttpApiTriggerConfig;
                    if (apiParameters.references && apiParameters.references.length) {
                        return apiParameters.references;
                    }
                    const spec = apiParameters.apiSpec;
                    if (!spec) {
                        return []; // no refs if the spec hasn't been selected yet
                    }
                    const ret = [
                        "data",
                        "data.username",
                        "data.httpBody",
                        "data.httpMethod",
                        "data.httpRequestUri",
                        "data.httpClientIp",
                        "data.httpCookies",
                        "data.body",
                        "data.path",
                        "data.query",
                        "data.header",
                        "claimsprincipal",
                        "claimsprincipal.Identity",
                        "claimsprincipal.Identity.Name",
                        "claimsprincipal.Claims"
                    ].concat(getParametersFromSwaggerDefinition(spec));

                    // TODO: Support for array auto complete? Might have to be context aware
                    return ret;

                }
            case Triggers.ConditionalTrigger.triggerType:
                {
                    return ["data", "data.result"];
                }
            default:
                return [];
        }
    });
    // Flatten arrays
    let dataRefs = triggers.length > 0
        ? triggers.reduce((a, b) => a.concat(b))
        : [];

    if (!_.isEmpty(manualTriggerParameterNames) && manualTriggerParameterNames !== null) {
        dataRefs.push("data");
        dataRefs = dataRefs.concat(manualTriggerParameterNames.map((n) => "data." + n));
    }
    return _.uniq(dataRefs.concat(constantTriggerReferences));
}

function getParametersFromSwaggerDefinition(spec: Spec): string[] {
    const parameters = {
        query: [] as string[],
        form: [] as string[],
        header: [] as string[],
        path: [] as string[],
        body: [] as string[]
    };
    const paths = spec.paths;

    // Only one path and one method should be present:
    const pathKey = Object.keys(paths)[0];
    const path = paths[pathKey] as Path;
    const methodKey = Object.keys(path)[0];
    const method = (path as { [key: string]: Operation })[methodKey];
    if (method.parameters) {

        for (const p of method.parameters) {
            try {
                let param: any = p;
                if (param.$ref) { // actually, the parameter may be a $ref
                    param = getElementByJsonReference(param.$ref, spec).element;
                }
                switch (param.in) {
                    case "body": // only body parameters should have recursuve schemas
                        parameters.body = parameters.body
                            .concat(getParametersFromSchemaRecursive(param.schema, spec, "body", 0));
                        break;
                    case "path":
                        parameters.path.push(getSubRef("path", (param as PathParameter).name));
                        break;
                    case "query":
                        parameters.query.push(getSubRef("query", (param as QueryParameter).name));
                        break;
                    case "header":
                        parameters.header.push(getSubRef("header", (param as HeaderParameter).name));
                        break;
                }
            } catch (error) {
                // tslint:disable-next-line:no-console
                console.error(`Error when parsing references from Swagger definition for '${spec.basePath}${pathKey} ${methodKey} ${p && (p as any).name}'`, error);
            }
        }
    }
    return _.flatten(Object.keys(parameters).map((k) => (parameters as { [key: string]: string[] })[k].map((p) => "data." + p)));
}

function getSubRef(name: string, ref: string) {
    // check for valid property ref that can be compiled:
    // regex from https://stackoverflow.com/a/28749144
    if (!/^[a-zA-Z]+[a-zA-Z0-9\_]*$/.test(ref)) {
        return `${name || ""}["${ref}"]`; // not valid property ref -> return accessor
    }
    if (name) {
        return name + "." + ref;
    }
    return ref;
}

function getParametersFromSchemaRecursive(schema: Schema, spec: Spec, parentKey: string, depth: number): string[] {
    let ret = [] as string[];
    if (!schema || depth > 5) { // go max five levels deep
        return ret;
    }
    if (schema.properties) {
        for (const name of Object.keys(schema.properties)) {
            const property = schema.properties[name];
            const keyPath = getSubRef(parentKey, name);
            ret.push(keyPath);
            const recursiveProps = getParametersFromSchemaRecursive(property, spec, keyPath, depth + 1);
            ret = ret.concat(recursiveProps);
        }
    }
    if (schema.$ref) {
        const referencedSchema = getElementByJsonReference(schema.$ref, spec);
        const recursivePaths = getParametersFromSchemaRecursive(referencedSchema.element, spec, parentKey, depth + 1);
        ret = ret.concat(recursivePaths);
    }

    return ret;
}

export function getProcessReferences(): string[] {
    return ["uri", "id", "version", "name", "executionid", "agent", "agentGroup", "environment", "cancellationToken"];
}
